import * as cookie from 'cookie'
import React, { useState } from 'react'
import { useHydrated } from 'remix-utils/use-hydrated'
import { Button } from '#app/components/ui/button.tsx'
import { Checkbox } from '#app/components/ui/checkbox.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	COOKIE_EXPIRY_DAYS,
	COOKIE_NAME,
	type CookiePreferences,
	getCookiePreferences,
} from '#app/utils/cookie-consent.ts'

function initializeScripts() {
	// TODO: initialize scripts here without reloading
	window.location.reload()
}

const CookieConsent: React.FC = () => {
	const hydrated = useHydrated()
	const consent = hydrated ? getCookiePreferences() : null
	const [openOverride, setIsOpen] = useState<boolean | null>(null)
	const isOpen = openOverride ?? (hydrated && !consent)
	const [changingConsent, setChangingConsent] = useState(false)
	const [draftPreferences, setPreferences] = useState<CookiePreferences | null>(
		null,
	)
	const preferences = draftPreferences ??
		consent ?? {
			preferences: false,
			statistics: false,
			marketing: false,
		}

	const handleCheckChanged = (
		category: keyof CookiePreferences,
		checked: boolean | 'indeterminate',
	) => {
		setPreferences({
			...preferences,
			[category]: !!checked,
		})
	}

	const setConsent = (preferences: CookiePreferences) => {
		setConsentCookie(preferences)
		setIsOpen(false)
		initializeScripts()
	}

	const acceptAll = () =>
		setConsent({
			preferences: true,
			statistics: true,
			marketing: true,
		})

	const savePreferences = () => setConsent(preferences)
	const declineAll = () =>
		setConsent({
			preferences: false,
			statistics: false,
			marketing: false,
		})

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen} modal={true}>
			<DialogTrigger asChild>
				<Button
					size="sm"
					variant="outline"
					onClick={() => {
						setChangingConsent(true)
						setIsOpen(true)
					}}
				>
					Cookie Settings <Icon name="cookie" className="align ml-2" />
				</Button>
			</DialogTrigger>
			<DialogContent
				showCloseButton={false}
				onInteractOutside={(e) => e.preventDefault()}
				className="max-h-[80vh] overflow-y-auto sm:max-w-lg"
			>
				<DialogHeader>
					<DialogTitle>
						{changingConsent
							? 'Manage Cookie Settings'
							: 'We Value Your Privacy'}
					</DialogTitle>
					<DialogDescription>
						{changingConsent
							? 'Set your cookie preferences to control the types of cookies we use.'
							: 'We use cookies to enhance your experience, analyze site traffic, and for marketing purposes. Please manage your preferences below.'}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 px-4">
					<div>
						<label className="flex items-center">
							<Checkbox checked={true} disabled={true} className="mr-2" />
							<span className="font-medium">Necessary Cookies</span>
						</label>
						<p className="text-text-secondary mt-1 ml-6 text-sm">
							These cookies are essential for the website to function properly.
						</p>
					</div>

					<div>
						<label className="flex items-center">
							<Checkbox
								checked={preferences.preferences}
								onCheckedChange={(isChecked) =>
									handleCheckChanged('preferences', isChecked)
								}
								className="mr-2"
							/>
							<span className="font-medium">Preferences Cookies</span>
						</label>
						<p className="text-text-secondary mt-1 ml-6 text-sm">
							These cookies allow the website to remember choices you make.
						</p>
					</div>

					<div>
						<label className="flex items-center">
							<Checkbox
								checked={preferences.statistics}
								onCheckedChange={(isChecked) =>
									handleCheckChanged('statistics', isChecked)
								}
								className="mr-2"
							/>
							<span className="font-medium">Statistics Cookies</span>
						</label>
						<p className="text-text-secondary mt-1 ml-6 text-sm">
							These cookies help us understand how visitors interact with our
							website.
						</p>
					</div>

					<div>
						<label className="flex items-center">
							<Checkbox
								checked={preferences.marketing}
								onCheckedChange={(isChecked) =>
									handleCheckChanged('marketing', isChecked)
								}
								className="mr-2"
							/>
							<span className="font-medium">Marketing Cookies</span>
						</label>
						<p className="text-text-secondary mt-1 ml-6 text-sm">
							These cookies are used to deliver relevant advertisements to you.
						</p>
					</div>
				</div>

				<DialogFooter className="mt-6 flex flex-wrap gap-2 px-4">
					<Button variant="secondary" onClick={acceptAll}>
						Accept All
					</Button>
					<Button variant="default" onClick={savePreferences}>
						Save
					</Button>
					<Button variant="secondary" onClick={declineAll}>
						Decline All
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

function setConsentCookie(preferences: CookiePreferences) {
	const inOneYear = new Date(Date.now() + COOKIE_EXPIRY_DAYS * 864e5)
	document.cookie = cookie.stringifySetCookie(
		{
			name: COOKIE_NAME,
			value: JSON.stringify(preferences),
			expires: inOneYear,
			path: '/',
			sameSite: 'lax',
			secure: process.env.NODE_ENV === 'production',
		},
		{ encode: encodeURIComponent },
	)
	const cookies = cookie.parseCookie(document.cookie)
	const aLongTimeAgo = new Date(0)
	Object.entries(preferences).forEach(([category, allowed]) => {
		if (!allowed) {
			Object.entries(cookies).forEach(([cookieName]) => {
				if (cookieName.startsWith(`tv-${category}-`)) {
					document.cookie = cookie.stringifySetCookie({
						name: cookieName,
						value: 'no-longer-allowed',
						expires: aLongTimeAgo,
					})
				}
			})
		}
	})
}

export default CookieConsent
